import { ChevronDownIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { useState } from 'react';
import { Dropdown } from 'react-daisyui';
import { useWorkspaceSwitch } from '../../../hooks/useWorkspaceSwitch.hook.ts';
import { useWorkspaceContext } from '../../../providers/WorkspaceProvider/Workspace.context.ts';
import { randomString } from '../../../functions/string.functions.ts';

/**
 * Workspace switcher in the navbar title slot.
 *
 * The navbar already rendered `currentWorkspace.name` here as static text —
 * this makes that same name the control, which is why it reads as "more air
 * and easier to reach" than the sidebar entry without adding a new surface.
 *
 * Deliberately NOT a status indicator: it shows the workspace name only. A
 * connection dot here would have to speak for a channel this component does
 * not own, and a green dot that outlives the connection is worse than no dot.
 *
 * The sidebar selector stays as-is; both call the same `useWorkspaceSwitch`.
 */
export default function NavbarWorkspaceSelect() {
  const { workspaces, currentWorkspace } = useWorkspaceContext();
  const { switchWorkspace } = useWorkspaceSwitch();
  const [isOpen, setIsOpen] = useState(false);

  const workspaceList = workspaces?.workspaces ?? [];

  // One workspace (the common case) has nothing to switch to — render the
  // plain title so a dropdown affordance never promises a menu of one.
  if (workspaceList.length <= 1) {
    return <h1 className="Navbar__Title">{currentWorkspace?.name}</h1>;
  }

  return (
    <Dropdown
      className="Navbar__WorkspaceSelect"
      data-testid="navbar_workspace_selector_dropdown"
    >
      {/* button={false} because the toggle wraps the workspace name, which is
          an <h1> — flow content, invalid inside a <button>. That loses the
          native button's keyboard behaviour, so it is restored explicitly:
          focusable, operable with Enter/Space, and announced as an expandable
          menu button.

          CRisPR suggested instead lifting the <h1> out so the toggle can be a
          real <button>. That was not taken because it would shrink the hit
          target to the chevron alone — clicking the workspace name is the whole
          point of this control, since the name was already sitting there inert
          before it became the switcher. */}
      <Dropdown.Toggle
        className="Navbar__WorkspaceSelect__Toggle"
        button={false}
        role="button"
        tabIndex={0}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        data-testid="navbar_workspace_selector"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            // Space would otherwise scroll the page.
            event.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        <h1 className="Navbar__Title">{currentWorkspace?.name}</h1>
        <ChevronDownIcon className="Navbar__WorkspaceSelect__Icon" />
      </Dropdown.Toggle>

      {isOpen ? (
        <Dropdown.Menu
          className="Navbar__WorkspaceSelect__Menu"
          data-testid="navbar_workspace_selector_menu"
        >
          {workspaceList.map((workspace) => (
            <Dropdown.Item
              key={randomString()}
              className={classNames('Navbar__WorkspaceSelect__Item', {
                'btn-active': workspace.id === currentWorkspace?.id,
              })}
              data-testid="navbar_workspace_selector_item"
              onClick={() => {
                setIsOpen(false);
                switchWorkspace(workspace.id);
              }}
            >
              <div className="Navbar__WorkspaceSelect__ShortName">
                {workspace.name[0]}
              </div>
              <p className="truncate">{workspace.name}</p>
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      ) : null}
    </Dropdown>
  );
}
